import Autocomplete from "@mui/material/Autocomplete";
import Box from "@mui/material/Box";
import IconButton from "@mui/material/IconButton";
import TextField from "@mui/material/TextField";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import SettingsIcon from "@mui/icons-material/Settings";
import React, { useEffect, useState } from "react";
import useOtDevices from "../hooks/useOtDevices";
import useOtSignalSearch from "../hooks/useOtSignalSearch";
import IntelligenceTimeFilter from "./IntelligenceTimeFilter";
import SignalConfigDialog from "./SignalConfigDialog";

// ─── Scrollable listbox for the signal Autocomplete ───────────────────────────

const ITEM_SIZE = 56;
const MAX_VISIBLE = 10;

const VirtualizedListbox = React.forwardRef(function VirtualizedListbox(props, ref) {
  const { children, ...other } = props;
  return (
    <ul
      ref={ref}
      {...other}
      style={{
        maxHeight: MAX_VISIBLE * ITEM_SIZE,
        overflowY: "auto",
        padding: 0,
        margin: 0,
        listStyle: "none",
      }}
    >
      {children}
    </ul>
  );
});

/**
 * Device + signal selection, shared by both OT Monitor tabs (Statistics and Chart & Table) —
 * rendered once, above the tabs, so the selection is global instead of living inside one tab
 * and resetting/being unreachable from the other.
 *
 * Controlled: the parent owns selectedDevice/selectedSignal so both tabs can read them.
 *
 * @param {string|null}   selectedDevice   - currently selected device ip
 * @param {Function}      onDeviceChange   - (ip) => void
 * @param {string|null}   selectedSignal   - currently selected signal's full_address
 * @param {Function}      onSignalChange   - (fullAddress, meta) => void — meta is the full signal
 *                                          object ({ name, protocol, address, ... }) or null
 * @param {Object|null}   dateFrom         - dayjs — current range start, mirrors IntelligenceTimeFilter
 * @param {Object|null}   dateTo           - dayjs — current range end
 * @param {Function|null} onDateChange     - ({dateFrom, dateTo}) => void; when omitted, no time
 *                                          filter is rendered
 */
const OtDeviceSignalSelector = ({
  selectedDevice = null,
  onDeviceChange,
  selectedSignal = null,
  onSignalChange,
  dateFrom = null,
  dateTo = null,
  onDateChange = null,
}) => {
  const [tagInputValue, setTagInputValue] = useState("");
  const [configOpen, setConfigOpen] = useState(false);

  // ── Level 1: device list ──
  const { devices, loading: devicesLoading } = useOtDevices();

  // Auto-select first device
  useEffect(() => {
    if (!devices.length || selectedDevice) return;
    onDeviceChange(devices[0].ip);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [devices]);

  // ── Level 2: tag search (server-side, debounced) ──
  const { signals: availableSignals, loading: signalsLoading } = useOtSignalSearch(
    selectedDevice,
    tagInputValue,
  );

  // Reset signal when device changes
  useEffect(() => {
    onSignalChange(null, null);
    setTagInputValue("");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedDevice]);

  // Clear the signal if it fell out of the (re-searched) list
  useEffect(() => {
    if (!availableSignals.length || !selectedSignal) return;
    if (!availableSignals.some((s) => s.full_address === selectedSignal)) {
      onSignalChange(null, null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [availableSignals]);

  const selectedSignalObj = availableSignals.find((s) => s.full_address === selectedSignal) ?? null;

  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 2.5, flexWrap: "wrap" }}>
      <Typography variant="h6" sx={{ fontWeight: 600, color: "text.primary" }}>
        Device &amp; Signal
      </Typography>

      {/* Level 1 — device selector */}
      <Autocomplete
        size="small"
        loading={devicesLoading}
        options={devices}
        value={devices.find((d) => d.ip === selectedDevice) ?? null}
        onChange={(_, val) => onDeviceChange(val?.ip ?? null)}
        getOptionLabel={(d) => d.ip}
        isOptionEqualToValue={(a, b) => a.ip === b.ip}
        renderOption={(props, d) => (
          <Box component="li" {...props} key={d.ip}
            sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 2 }}
          >
            <Box>
              <Typography variant="body2" sx={{ fontWeight: 600, color: "#eee", fontFamily: "monospace" }}>
                {d.ip}
              </Typography>
              {d.protocol && (
                <Typography sx={{ fontSize: 10, color: "#666", textTransform: "uppercase", letterSpacing: "0.05em" }}>
                  {d.protocol}
                </Typography>
              )}
            </Box>
            <Box sx={{ px: 0.8, py: 0.2, bgcolor: "#45909520", border: "1px solid #45909550", borderRadius: 0.5, flexShrink: 0 }}>
              <Typography sx={{ fontSize: 10, color: "#459095", fontFamily: "monospace" }}>
                {d.signalCount ?? 0}
              </Typography>
            </Box>
          </Box>
        )}
        renderInput={(params) => <TextField {...params} label="Device" />}
        sx={{ minWidth: 200 }}
        slotProps={{
          paper: {
            sx: {
              bgcolor: "#1e1e1e", border: "1px solid #333", color: "#eee",
              "& .MuiAutocomplete-listbox": { padding: 0 },
            },
          },
        }}
      />

      {/* Level 2 — tag selector: server-side search + virtualized + groupBy area */}
      <Autocomplete
        size="small"
        disabled={!selectedDevice}
        loading={signalsLoading}
        options={availableSignals}
        value={selectedSignalObj}
        onChange={(_, newVal) => onSignalChange(newVal?.full_address ?? null, newVal ?? null)}
        inputValue={tagInputValue}
        onInputChange={(_, val) => setTagInputValue(val)}
        filterOptions={(x) => x}
        getOptionLabel={(s) => s.name ?? s.full_address}
        isOptionEqualToValue={(a, b) => a.full_address === b.full_address}
        ListboxComponent={VirtualizedListbox}
        renderOption={(props, s) => (
          <Box component="li" {...props} key={s.full_address}
            sx={{
              display: "flex", flexDirection: "column",
              alignItems: "flex-start !important",
              "&:hover": { bgcolor: "#2a2a2a !important" },
            }}
          >
            <Typography variant="body2" sx={{ fontWeight: 600, color: "#eee", lineHeight: 1.3 }}>
              {s.name}
            </Typography>
            <Box sx={{ display: "flex", gap: 1, alignItems: "center", mt: 0 }}>
              <Typography sx={{ fontFamily: "monospace", fontSize: 10, color: "#459095" }}>
                {s.address ?? "—"}
              </Typography>
              <Typography sx={{ fontSize: 10, color: "#555" }}>·</Typography>
              <Typography sx={{ fontSize: 10, color: "#666", textTransform: "uppercase", letterSpacing: "0.05em" }}>
                {s.protocol ?? "—"}
              </Typography>
            </Box>
          </Box>
        )}
        renderInput={(params) => (
          <TextField {...params} label={!selectedDevice ? "Select a device first" : "Signal (first 200 — type to filter)"} />
        )}
        sx={{ minWidth: 280 }}
        slotProps={{
          paper: {
            sx: {
              bgcolor: "#1e1e1e",
              border: "1px solid #333",
              color: "#eee",
              minHeight: ITEM_SIZE + 32,
              "& .MuiAutocomplete-listbox": { padding: 0 },
            },
          },
        }}
      />

      {/* Gear next to the selected signal — jumps straight to that signal's own
          config (name/unit/thresholds) instead of making the user find it again
          in the full OT Tag Configuration table by IP + protocol + address. */}
      {selectedSignalObj && (
        <Tooltip title="Configure this signal">
          <IconButton size="small" onClick={() => setConfigOpen(true)} sx={{ color: "#459095" }}>
            <SettingsIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      )}

      <SignalConfigDialog
        open={configOpen}
        onClose={() => setConfigOpen(false)}
        device={selectedDevice}
        signal={selectedSignalObj}
      />

      {/* Time range — same row/level as the device & signal selectors. Wrapped with a negative
          margin to cancel IntelligenceTimeFilter's own internal mb:2, which otherwise pushes
          its visible content (chips/picker/button) up out of vertical alignment with the
          selectors when both share this flex row's centered cross-axis. */}
      {onDateChange && (
        <Box sx={{ mb: -2 }}>
          <IntelligenceTimeFilter
            compact
            title=""
            disabledPresets={["30D"]}
            initialDateFrom={dateFrom}
            initialDateTo={dateTo}
            onDateChange={onDateChange}
          />
        </Box>
      )}
    </Box>
  );
};

export default OtDeviceSignalSelector;
